import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, Calendar, Building2, Clock, Package, StickyNote, Pencil, Copy, Trash2, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { ReportExportButton } from "../components/ui/report-export-button";
const DailyReportPage = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedJob, setSelectedJob] = useState("");
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await apiClient.get(`/jobs/for-daily-entry`);
        setJobs(response.data.filter((j) => j.status === "active" || j.status === "complete"));
      } catch (error) {
        toast.error("Failed to load jobs");
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [getAuthHeaders]);

  const generateReport = async () => {
    if (!selectedJob) {
      toast.error("Please select a job");
      return;
    }

    setReportLoading(true);
    try {
      const response = await apiClient.get(
        `/reports/daily?date=${selectedDate}&job_id=${selectedJob}`
      );
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate report");
    } finally {
      setReportLoading(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    setDeletingEntryId(entryId);
    try {
      await apiClient.delete(`/daily-entries/${entryId}`);
      toast.success("Entry deleted");
      // Refresh the report
      await generateReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete entry");
    } finally {
      setDeletingEntryId(null);
    }
  };

  const handleEditEntry = (entryId) => {
    navigate(`/daily-entry?edit=${entryId}`);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const y = date.getFullYear();
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    return `${weekday}, ${m}/${d}/${y}`;
  };

  const formatTime = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        hour: "numeric", minute: "2-digit", hour12: true
      });
    } catch {
      return "";
    }
  };

  const getExcelData = useCallback(() => {
    if (!report) return null;
    let csv = `Daily Report - ${report.job_name}\n`;
    csv += `Date: ${formatDate(report.date)}\n`;
    csv += `General Contractor: ${report.client_name}\n\n`;
    csv += "HOURS REPORT\n";
    csv += "Employee,Hours\n";
    report.employee_hours.forEach((emp) => {
      csv += `${emp.name},${emp.hours}\n`;
    });
    csv += `Total,${report.total_hours}\n\n`;
    csv += "MATERIALS USED\n";
    csv += "Material,Quantity\n";
    report.materials_used.forEach((mat) => {
      csv += `${mat.name},${mat.quantity}\n`;
    });
    if (report.notes.length > 0) {
      csv += "\nNOTES\n";
      report.notes.forEach((n) => {
        csv += `${n.by}: ${n.note}\n`;
      });
    }
    return { filename: `daily-report-${report.job_name.replace(/\s+/g, "-")}-${report.date}`, csvContent: csv };
  }, [report]);

  const getPdfContent = useCallback(() => {
    if (!report) return null;
    const customStyles = `
      .header-info { color: #666; margin-bottom: 5px; }
      .date { font-size: 16px; font-weight: bold; margin: 10px 0 20px 0; }
      h2 { border-bottom: 2px solid #2563eb; padding-bottom: 5px; }
      .total-row { background-color: #e5e7eb !important; font-weight: bold; }
      .note-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin: 8px 0; }
      .note-by { font-weight: bold; color: #666; font-size: 12px; margin-bottom: 4px; }
      .note-text { color: #333; }
      .empty-state { color: #888; text-align: center; padding: 20px; font-style: italic; }
    `;
    const htmlContent = `
      <h1>${report.job_name}</h1>
      <p class="header-info">General Contractor: ${report.client_name}</p>
      <p class="date">${formatDate(report.date)}</p>
      <h2>Hours Report</h2>
      ${report.employee_hours.length === 0 
        ? '<p class="empty-state">No hours recorded for this date</p>'
        : `<table>
            <thead><tr><th>Employee</th><th class="text-right">Hours</th></tr></thead>
            <tbody>
              ${report.employee_hours.map(emp => `<tr><td>${emp.name}</td><td class="text-right">${emp.hours}</td></tr>`).join('')}
              <tr class="total-row"><td>Total Hours</td><td class="text-right">${report.total_hours}</td></tr>
            </tbody>
          </table>`
      }
      <h2>Materials Used</h2>
      ${report.materials_used.length === 0 
        ? '<p class="empty-state">No materials recorded for this date</p>'
        : `<table>
            <thead><tr><th>Material</th><th class="text-right">Quantity</th></tr></thead>
            <tbody>
              ${report.materials_used.map(mat => `<tr><td>${mat.name}</td><td class="text-right">${mat.quantity}</td></tr>`).join('')}
            </tbody>
          </table>`
      }
      ${report.notes.length > 0 ? `
        <h2>Notes</h2>
        ${report.notes.map(note => `<div class="note-box"><p class="note-by">${note.by}</p><p class="note-text">${note.note}</p></div>`).join('')}
      ` : ''}
      <p class="footer">V2R Masonry - Daily Report | Generated ${new Date().toLocaleString()}</p>
    `;
    return { title: `Daily Report - ${report.job_name}`, htmlContent, customStyles };
  }, [report]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const entries = report?.entries || [];
  const hasDuplicates = entries.length >= 2;

  return (
    <div className="page-enter space-y-8" data-testid="daily-report-page">
      <style hidden>{`
        @media print {
          body * { visibility: hidden; }
          #report-content, #report-content * { visibility: visible; }
          #report-content { position: absolute; left: 0; top: 0; width: 100%; }
          .print\\:hidden { display: none !important; }
          .report-card { page-break-inside: avoid; break-inside: avoid; margin-bottom: 16px; }
        }
      `}</style>
      
      {/* Header */}
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">
            Daily Report
          </h1>
        </div>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="form-group">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Select Date
              </Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="report-date-input"
              />
            </div>
            <div className="form-group">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Select Job
              </Label>
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger data-testid="report-job-select">
                  <SelectValue placeholder="Choose a job..." />
                </SelectTrigger>
                <SelectContent>
                  {[...jobs].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.name} ({job.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={generateReport} 
              disabled={!selectedJob || reportLoading}
              data-testid="generate-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {reportLoading ? "Loading..." : "Generate Report"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Output */}
      {report && (
        <div className="space-y-6 print:space-y-4" id="report-content">
          {/* Report Header */}
          <Card className="print:shadow-none print:border-0">
            <CardHeader className="flex flex-row items-center justify-between print:pb-2">
              <div>
                <CardTitle className="text-2xl">{report.job_name}</CardTitle>
                <p className="text-muted-foreground">GC: {report.client_name}</p>
                <p className="text-lg font-medium mt-2">{formatDate(report.date)}</p>
              </div>
              <div className="flex gap-2 print:hidden">
                {hasDuplicates && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDuplicatesOpen(true)}
                    data-testid="show-duplicates-btn"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Show Duplicates ({entries.length})
                  </Button>
                )}
                {entries.length === 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditEntry(entries[0].id)}
                    data-testid="edit-report-btn"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                )}
                {entries.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDuplicatesOpen(true)}
                    data-testid="edit-report-multi-btn"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                )}
                <ReportExportButton
                  title="Export Daily Report"
                  getExcelData={getExcelData}
                  getPdfContent={getPdfContent}
                  size="sm"
                />
              </div>
            </CardHeader>
          </Card>

          {/* Hours Report */}
          <Card className="print:shadow-none print:border print:border-gray-300">
            <CardHeader className="print:py-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Hours Report
              </CardTitle>
            </CardHeader>
            <CardContent className="print:pt-0">
              {report.employee_hours.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No hours recorded for this date</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.employee_hours.map((emp, idx) => (
                      <TableRow key={idx} className="table-row">
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell className="text-right">{emp.hours}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/30 font-bold">
                      <TableCell>Total Hours</TableCell>
                      <TableCell className="text-right">{report.total_hours}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Materials Used */}
          <Card className="print:shadow-none print:border print:border-gray-300">
            <CardHeader className="print:py-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Materials Used
              </CardTitle>
            </CardHeader>
            <CardContent className="print:pt-0">
              {report.materials_used.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No materials recorded for this date</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Material</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.materials_used.map((mat, idx) => (
                      <TableRow key={idx} className="table-row">
                        <TableCell className="font-medium">{mat.name}</TableCell>
                        <TableCell className="text-right">{mat.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {report.notes.length > 0 && (
            <Card className="print:shadow-none print:border print:border-gray-300">
              <CardHeader className="print:py-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <StickyNote className="w-5 h-5" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="print:pt-0">
                <div className="space-y-3">
                  {report.notes.map((note, idx) => (
                    <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground">{note.by}</p>
                      <p className="mt-1">{note.note}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!report && (
        <Card className="p-12">
          <div className="text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No Report Generated
            </h3>
            <p className="text-muted-foreground">
              Select a date and job, then click &quot;Generate Report&quot; to view the daily summary.
            </p>
          </div>
        </Card>
      )}

      {/* Duplicates Dialog */}
      <Dialog open={duplicatesOpen} onOpenChange={setDuplicatesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Daily Entries for {report ? formatDate(report.date) : ""}
              <Badge variant="secondary" className="ml-1">{entries.length} entries</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {entries.map((entry, idx) => (
              <Card key={entry.id} className="border">
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">Entry #{idx + 1}</span>
                        <Badge variant="outline" className="text-xs">
                          {entry.created_by_name || "Unknown"}
                        </Badge>
                      </div>
                      {entry.created_at && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Submitted: {formatTime(entry.created_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDuplicatesOpen(false);
                          handleEditEntry(entry.id);
                        }}
                        data-testid={`edit-duplicate-${entry.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={deletingEntryId === entry.id}
                            data-testid={`delete-duplicate-${entry.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {deletingEntryId === entry.id ? "Deleting..." : "Delete"}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete This Entry?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this daily entry and revert its labor/material costs from the job. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteEntry(entry.id)}
                              className="bg-destructive text-destructive-foreground"
                              data-testid={`confirm-delete-duplicate-${entry.id}`}
                            >
                              Delete Entry
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>

                  {/* Employees & Hours */}
                  {(entry.employees || []).length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Employees
                      </p>
                      <div className="space-y-1">
                        {entry.employees.map((emp, eIdx) => (
                          <div key={eIdx} className="flex items-center justify-between text-sm px-2 py-1 bg-muted/30 rounded">
                            <span>{emp.employee_name}</span>
                            <span className="font-medium">{emp.hours}h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Materials */}
                  {(entry.employees || []).some(emp => (emp.materials || []).length > 0) && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                        <Package className="w-3 h-3" /> Materials
                      </p>
                      <div className="space-y-1">
                        {entry.employees.flatMap(emp =>
                          (emp.materials || []).filter(m => m.quantity > 0).map((mat, mIdx) => (
                            <div key={`${emp.employee_id}-${mIdx}`} className="flex items-center justify-between text-sm px-2 py-1 bg-muted/30 rounded">
                              <span>{mat.material_name}</span>
                              <span className="font-medium">{mat.quantity}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {entry.notes && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center gap-1">
                        <StickyNote className="w-3 h-3" /> Notes
                      </p>
                      <p className="text-sm bg-muted/30 rounded px-2 py-1">{entry.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyReportPage;
